<template>
  <div class="-scene-detection">
    <div class="-top">
      <wt-toggle
        class="-toggle wt2-text-n"
        label-left="Suggested Scenes"
        v-model="enabled"
      />
      <div v-if="enabled" class="wt2-text-n" v-text="sceneCounterText" />
    </div>

    <wt-carousel-gallery
      v-if="enabled && scenes.length"
      class="-carousel"
      :bus="bus"
      :customNavigation="true"
    >
      <div class="-carousel-inner">
        <scene-detection-thumbnail
          v-for="(scene, i) in scenes"
          :key="scene.guid"
          ref="sceneThumbnails"
          :scene="scene"
          :is-selected="isSelectedScene(i)"
          :is-scene-at-playhead="isSceneAtPlayhead(i)"
          @selectScene="selectScene"
        />
      </div>
    </wt-carousel-gallery>
  </div>
</template>

<script>
import bus from 'v/AssetEditor/AssetEditorEventBus';
import { mapMutations, mapState } from 'vuex';
import app from 'app';
import SceneDetectionThumbnail from 'AssetEditor/functionality/VideoTrimming/SceneDetectionThumbnail.vue';

export default {
  name: 'SceneDetection',
  components: {
    SceneDetectionThumbnail,
  },

  props: {
    src: { type: String, required: true },
    selectedScene: { type: Object, required: false, default: null },
    // TODO: link between playhead position and scene
    sceneAtPlayhead: { type: Object, required: false, default: null },
  },

  data() {
    const userChoice = app.stash.get('fragmentEditorShowScene');
    const enabled = typeof userChoice === 'boolean' ? userChoice : true;

    return {
      enabled,
      scenes: [],
      bus,
      loadingVideo: true,
      loadingScenes: true,
    };
  },

  computed: {
    ...mapState('assetEditor', ['workModel']),

    sceneCounterText() {
      if (this.loadingScenes) {
        return '';
      } else {
        return this.scenes.length ? this.scenes.length : 'No scenes';
      }
    },
  },

  beforeDestroy() {
    this.bus.$off(
      'ENSURE_SELECTED_SCENE_PAGE_VISIBLE',
      this.$_onEnsureSelectedSceneVisible
    );
  },

  created() {
    this.getScenes().then(
      (res) => {
        this.scenes = res;
        this.loadingScenes = false;
        this.capture();
      },
      () => {
        return this.postScenes().then(
          (res) => {
            this.scenes = res;
            this.loadingScenes = false;
            this.capture();
          },
          () => {
            this.scenes = [];
            this.loadingScenes = false;
          }
        );
      }
    );

    this.bus.$on(
      'ENSURE_SELECTED_SCENE_PAGE_VISIBLE',
      this.$_onEnsureSelectedSceneVisible
    );
  },

  watch: {
    scenes() {
      if (this.enabled) {
        this.$emit('scenes-update', this.scenes);
      }
    },

    enabled: {
      immediate: true,
      handler(enabled) {
        this.$emit('scenes-update', enabled ? this.scenes : []);
        this.setSceneDetectionEnabled(enabled);
      },
    },
  },

  methods: {
    ...mapMutations('assetEditor', ['setSceneDetectionEnabled']),

    $_onEnsureSelectedSceneVisible() {
      const $thumb = _.find(this.$refs.sceneThumbnails, (vm) => {
        return vm.isSelected;
      });

      if ($thumb) {
        this.bus.$emit('SCROLL_PAGE_IF_NEEDED', { $el: $thumb.$el });
      }
    },

    isSelectedScene(i) {
      return this.selectedScene?.guid === this.scenes[i].guid;
    },

    isSceneAtPlayhead(i) {
      return this.sceneAtPlayhead?.guid === this.scenes[i].guid;
    },

    selectScene(scene) {
      this.$emit('select', scene);
    },

    getScenes() {
      return new Promise((resolve, reject) => {
        app.api.sceneDetection
          .get(this.workModel.metadata.assetUUID)
          .then((scenes) => {
            scenes = _.filter(
              scenes,
              (scene) => scene.duration >= app.TIME.MIN_SCENE_DURATION
            );
            scenes = scenes.length > 1 ? scenes : [];
            resolve(scenes);
          }, reject);
      });
    },

    postScenes() {
      const uuid = this.workModel.metadata.assetUUID;

      return app.api.sceneDetection.post(uuid).then(
        () => {
          return new Promise((resolve, reject) => {
            app.api.realTime.listenAssetScenes(uuid).then(() => {
              resolve(this.getScenes(uuid));
            }, reject);
          });
        },
        () => []
      );
    },

    getFrame(time) {
      const video = this.video;
      const canvas = this.canvas;
      return new Promise((resolve) => {
        const seeked = function () {
          canvas
            .getContext('2d')
            .drawImage(video, 0, 0, canvas.width, canvas.height);
          resolve({ url: canvas.toDataURL('image/jpeg'), handler: seeked });
        };
        video.addEventListener('canplay', seeked);
        video.currentTime = time;
      }).then((obj) => {
        video.removeEventListener('canplay', obj.handler);
        return obj.url;
      });
    },

    async onCanPlay() {
      this.video.removeEventListener('canplay', this.onCanPlay);
      for (const scene of this.scenes) {
        const url = await this.getFrame(scene.startTime);
        this.$set(scene, 'src', url);
      }
      this.loadingVideo = false;
      setTimeout(() => {
        bus.$emit('FORCE_UPDATE_SCROLL_INFO');
      }, 10);
    },

    async capture() {
      const canvas = document.createElement('canvas');
      const video = document.createElement('video');
      this.video = video;
      this.canvas = canvas;
      video.volume = 0;
      video.src = this.src;
      video.crossOrigin = 'anonymous';
      video.pause();
      canvas.width = 97;
      canvas.height = 54;
      video.addEventListener('canplay', this.onCanPlay);
    },
  },
};
</script>

<style lang="scss" scoped>
.-scene-detection {
  user-select: none;
  margin-bottom: 30px;
}
.-top {
  display: flex;
  justify-content: space-between;
  padding-top: 10px;
  margin-bottom: 10px;
}
.-carousel-inner {
  display: inline-block;
  padding: 2px 2px 7px 2px;
}
.-toggle {
  width: 160px;
}

/deep/ .wt2-carousel-gallery .-arrow {
  top: calc(50% - 3px);
}

.-loader {
  position: relative;
  height: 100%;
  > div {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateY(-50%) translateX(-50%) scale(0.35);
  }
}

.-carousel {
  height: auto;
}
</style>
